/**
 * [C28] Error boundary (D-259): a crashed screen is reported (`render_crash`, with the screen's trace id and the
 * component stack) and replaced by the crash notice; the sidebar and header stay usable. Keyed by the section in
 * the shell, so opening another section starts clean.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { CrashNotice } from "@faylasuf/ui";
import { reportError, traceRef } from "./telemetry.js";

interface Props {
  readonly children: ReactNode;
  /** Rendered instead of the crash notice (the root boundary has no i18n provider around it). */
  readonly fallback?: (ref: string, retry: () => void) => ReactNode;
}
interface State {
  readonly failed: boolean;
  readonly ref: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false, ref: "" };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true, ref: traceRef() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError("render_crash", error, info.componentStack ? { componentStack: info.componentStack } : {});
  }

  private readonly retry = () => this.setState({ failed: false, ref: "" });

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback ? this.props.fallback(this.state.ref, this.retry) : <CrashNotice traceRef={this.state.ref} onRetry={this.retry} />;
  }
}
